import { useMemo, useRef } from 'react';
import { Alert, Button, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, useLocalization } from '@ui-framework/index';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  PageSection,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import usePresignedUploads from '@product-framework/components/DataProvider/usePresignedUploads';
import { useAppVersion } from '@product-framework/router/AppVersionContext';
import {
  createDocument,
  metadataPayload,
  pendingUploads,
  requireData,
  variantPayload,
} from '../../api';
import schema from '../../schemas/create.json';
import { uploadSchema } from '../../forms/schemas';
import { createValidationSchema } from '../../forms/validations';
import useDocumentSettings from '../../useDocumentSettings';

const FORM_ID = 'DOCUMENT_CREATE_FORM';
export default function DocumentManagementCreate() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const { appVersion, loading: versionLoading } = useAppVersion();
  const { settings, error, loading, refetch, dataProvider } =
    useDocumentSettings();
  const uploads = usePresignedUploads();
  const formSchema = useMemo(
    () => settings && uploadSchema(schema, settings, { appVersion }),
    [settings, appVersion],
  );
  const defaultValues = useMemo(
    () => ({
      appVersion,
      category: '',
      companyType: '',
      visibility: 'PRIVATE',
      status: 'ACTIVE',
      modules: [],
      serviceAdministratorIds: [],
      serviceProviderIds: [],
      languageCode: 'en',
      title: '',
      description: '',
      pdfFile: null,
      videoFile: null,
      thumbnailFile: null,
    }),
    [appVersion],
  );
  const validation = useMemo(
    () => createValidationSchema(formatMessage),
    [formatMessage],
  );
  const onSubmit = async values => {
    let created;
    try {
      created = requireData(
        await createDocument(dataProvider, {
          ...metadataPayload(values),
          variant: variantPayload(values),
        }),
      );
    } catch (failure) {
      return {
        nextValues: values,
        errors: failure.errors || [{ message: failure.message }],
      };
    }
    let uploadError = null;
    try {
      await uploads.runUploads(
        pendingUploads(
          dataProvider,
          created.id,
          created.variants[0].id,
          values,
        ),
      );
    } catch (failure) {
      uploadError = failure.message;
    }
    return {
      nextValues: defaultValues,
      errors: null,
      callback: () =>
        navigate(`/document-management/${created.id}`, {
          state: { uploadError },
        }),
    };
  };
  return (
    <Layout>
      <Breadcrumb
        items={[
          {
            name: formatMessage({
              id: 'DOCUMENT_MANAGEMENT',
              defaultMessage: 'Document Management',
            }),
            icon: IconLibrary.Help,
            onClick: () => navigate('/document-management'),
          },
          {
            name: formatMessage({
              id: 'NEW_DOCUMENT',
              defaultMessage: 'New Document',
            }),
            icon: IconLibrary.Note,
          },
        ]}
      />
      {(loading || versionLoading) && <LinearProgress />}
      {!versionLoading && !appVersion && (
        <Alert severity="error">
          {formatMessage({
            id: 'HELP_VERSION_UNAVAILABLE',
            defaultMessage:
              'Unable to determine the app version. Reload the page to try again.',
          })}
        </Alert>
      )}
      {uploads.uploading && (
        <LinearProgress
          variant="determinate"
          value={uploads.progress?.percent || 0}
          aria-label={uploads.progress?.fileName}
        />
      )}
      {uploads.uploading && (
        <ButtonContainer>
          <Button onClick={uploads.cancel}>
            {formatMessage({
              id: 'CANCEL_UPLOAD',
              defaultMessage: 'Cancel upload',
            })}
          </Button>
        </ButtonContainer>
      )}
      {error && (
        <Alert
          severity="error"
          action={
            <Button onClick={refetch}>
              {formatMessage({ id: 'RETRY', defaultMessage: 'Retry' })}
            </Button>
          }>
          {formatMessage({
            id: 'UPLOAD_SETTINGS_COULD_NOT_BE_LOADED',
            defaultMessage: 'Upload settings could not be loaded.',
          })}
        </Alert>
      )}
      {settings && !versionLoading && appVersion && (
        <PageSection
          label={formatMessage({
            id: 'NEW_DOCUMENT',
            defaultMessage: 'New Document',
          })}>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permittedByDefault
            schema={formSchema}
            defaultValues={defaultValues}
            validationSchema={validation}
            onSubmit={onSubmit}>
            <ButtonContainer>
              <Button
                variant="contained"
                disabled={uploads.uploading}
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
              </Button>
            </ButtonContainer>
          </Form>
        </PageSection>
      )}
    </Layout>
  );
}
